import { Component, Input, OnInit } from '@angular/core';
import { Assessment, AssessmentLogoEnum } from 'src/app/shared/models/assessment.model';
import { GrowthThresholds } from './GrowthThresholds';
import { BenchmarksService } from 'src/app/services/benchmarks/benchmarks.service';
import { AssessmentSummaryCategory } from 'src/app/shared/models/assessment-benchmarks.model';
import { CBMColor, NoNormingDataColor } from 'src/app/services/benchmarks/benchmark-bar-colors';
import { LicenseInfoService } from 'src/app/services/license-info/license-info.service';
import { FastBridgeLinkGeneratorService } from 'src/app/services/link-generators-v2/fastbridge-link-generator.service';
import { LaunchControlService } from 'src/app/services/launch-control/launch-control.service';
import { ProductAppCodes } from 'src/app/services/product-info/product-info.service';

export enum FastBridgeBenchmarkCategory {
  LOW_RISK = 'Low Risk',
  SOME_RISK = 'Some Risk',
  HIGH_RISK = 'High Risk',
  COLLEGE_PATHWAY = 'College Pathway',
  AT_GOAL_LINE = 'At Goal Line',
  BELOW_GOAL_LINE = 'Below Goal Line',
  ABOVE_GOAL_LINE = 'Above Goal Line'
}

export enum FastBridgeBenchmarkSwatch {
  LOW_RISK = '../../../../../assets/logos/fb-benchmark-swatches/low_risk.svg',
  SOME_RISK = '../../../../../assets/logos/fb-benchmark-swatches/some_risk.svg',
  HIGH_RISK = '../../../../../assets/logos/fb-benchmark-swatches/high_risk.svg',
  COLLEGE_PATHWAY = '../../../../../assets/logos/fb-benchmark-swatches/college_pathway.svg',
  AT_GOAL_LINE = '../../../../../assets/logos/fb-benchmark-swatches/at_goal_line.svg',
  BELOW_GOAL_LINE = '../../../../../assets/logos/fb-benchmark-swatches/below_goal_line.svg',
  ABOVE_GOAL_LINE = '../../../../../assets/logos/fb-benchmark-swatches/above_goal_line.svg'
}

@Component({
  selector: 'assessment-detail-result',
  templateUrl: './assessment-detail-result.component.html',
  styleUrls: ['./assessment-detail-result.component.scss']
})
export class AssessmentDetailResultComponent implements OnInit {
  @Input() assessment!: Assessment;
  @Input() studentId: string = '';
  @Input() isFastBridgeLicensed: boolean = false;
  public assessmentCategories: AssessmentSummaryCategory[] = [];
  public fastBridgeProgressMonitoringReportUrl: string = '';

  public constructor(
    private benchmarksService: BenchmarksService,
    private licenseInfoService: LicenseInfoService,
    private launchControlService: LaunchControlService,
    private fastBridgeLinkGeneratorService: FastBridgeLinkGeneratorService
  ) { }

  public async ngOnInit(): Promise<void> {
    await this.loadCategories();

    this.isFastBridgeLicensed = await this.licenseInfoService.currentClassesHaveFastBridge();
  }

  public isCBM(productId: string) {
    let cbmProducts = ['STAR_CBM_MATH', 'STAR_CBM_READING', 'STAR_CBM_LECTURA'];

    return cbmProducts.includes(productId);
  }

  public isFastBridge(logo: AssessmentLogoEnum | null): boolean {
    let fastbridgeLogo = AssessmentLogoEnum.FASTBRIDGE;

    return logo === fastbridgeLogo;
  }

  public getSGPText(currentSGP: number): string {
    if (currentSGP > 0) {
      return currentSGP.toString();
    }
    else {
      return '--';
    }
  }

  public getGrowthCategory(currentSGP: number): string {
    if (currentSGP >= GrowthThresholds.HIGH) {
      return "(High Growth)";
    }
    else if (currentSGP >= GrowthThresholds.MODERATE) {
      return "(Moderate Growth)";
    }
    else if (currentSGP >= GrowthThresholds.LOW) {
      return "(Low Growth)";
    }
    else {
      return "";
    }
  }

  public getStudentCategoryTitle(assessment: Assessment) {
    let filteredCategories = this.assessmentCategories.filter(x => x.productId === assessment.productId);

    let percentileRank = assessment.percentileRank;
    for (let category of filteredCategories) {
      if (percentileRank >= category.minPercentileRank && percentileRank <= category.maxPercentileRank) {
        return category.title;
      }
    };

    return 'No Norming Data';
  }

  public getStudentCategoryColor(assessment: Assessment) {
    let filteredCategories = this.assessmentCategories.filter(x => x.productId === assessment.productId);

    let percentileRank = assessment.percentileRank;
    for (let category of filteredCategories) {
      if (percentileRank >= category.minPercentileRank && percentileRank <= category.maxPercentileRank) {
        return category.color;
      }
    };

    return NoNormingDataColor;
  }

  public toPercent(decimal: number) {
    return Math.trunc(decimal * 100) + '%';
  }

  public generateCBMCategory(category: string | null) {
    if (category == null) {
      return 'Off-grade (no benchmark)';
    }
    return category;
  }

  public getCBMColor(category: string | null) {
    switch (category) {
      case 'Intervention':
      case 'Alert':
        return CBMColor.urgent;
      case 'At/Above Benchmark':
      case 'Satisfactory/Optimal':
        return CBMColor.atBenchmark;
      case 'On Watch':
        return CBMColor.onWatch;
      case null:
        return CBMColor.offGrade;
      default:
        return '';
    }
  }

  public getFastBridgeBenchmarkSwatch(category: string | null) {
    switch (category) {
      case FastBridgeBenchmarkCategory.LOW_RISK:
        return FastBridgeBenchmarkSwatch.LOW_RISK;
      case FastBridgeBenchmarkCategory.SOME_RISK:
        return FastBridgeBenchmarkSwatch.SOME_RISK;
      case FastBridgeBenchmarkCategory.HIGH_RISK:
        return FastBridgeBenchmarkSwatch.HIGH_RISK;
      case FastBridgeBenchmarkCategory.COLLEGE_PATHWAY:
        return FastBridgeBenchmarkSwatch.COLLEGE_PATHWAY;
      case FastBridgeBenchmarkCategory.AT_GOAL_LINE:
        return FastBridgeBenchmarkSwatch.AT_GOAL_LINE;
      case FastBridgeBenchmarkCategory.BELOW_GOAL_LINE:
        return FastBridgeBenchmarkSwatch.BELOW_GOAL_LINE;
      case FastBridgeBenchmarkCategory.ABOVE_GOAL_LINE:
        return FastBridgeBenchmarkSwatch.ABOVE_GOAL_LINE;
      default:
        return null;
    }
  }

  public launchFastBridgeProgressMonitoringReport() {
    const deepLink = this.fastBridgeLinkGeneratorService.getProgressMonitoringReportDeepLink(this.studentId, this.assessment.assessmentLongName);
    this.launchControlService.launchApp(ProductAppCodes.FastBridge, deepLink, true);
  }

  private async loadCategories() {
    let categories = await this.benchmarksService.getBenchmarkCategories();
    this.assessmentCategories = categories;
  }
}
